.wrap {
  position: relative;
  display: inline-block;
  width: 100%;
}

/* ── Trigger button ── */
.trigger {
  all: unset;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--space-2);
  width: 100%;
  height: 36px;
  padding: 0 var(--space-3);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  font-weight: var(--weight-regular);
  color: var(--text-muted);
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition:
    border-color var(--duration-fast) var(--ease-default),
    box-shadow var(--duration-fast) var(--ease-default),
    color var(--duration-fast) var(--ease-default);
  box-sizing: border-box;
}

.trigger:hover:not(:disabled) {
  border-color: var(--border-strong);
  color: var(--text-primary);
}
.trigger:hover:not(:disabled) .iconLeft,
.trigger:hover:not(:disabled) .chevron {
  color: var(--text-primary);
}

/* A filter that's been set (opt-in via highlightWhenSet): gold edge + icon,
   matching the gold "you set this" values in the scope sentence. */
.triggerSet {
  border-color: color-mix(in srgb, var(--active) 55%, var(--input-border));
  color: var(--text-primary);
}
.triggerSet .iconLeft {
  color: var(--active);
}

.triggerLabel {
  color: var(--text-muted);
}

.trigger:focus-visible,
.triggerOpen {
  border-color: var(--active);
  box-shadow: 0 0 0 3px var(--active-muted);
  outline: none;
  color: var(--text-primary);
}
.triggerOpen .iconLeft,
.triggerOpen .chevron {
  color: var(--active);
}

.trigger:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.triggerValue {
  flex: 1;
  text-align: left;
}

.placeholder {
  color: var(--input-placeholder);
}
.chevron {
  color: var(--text-muted);
  flex-shrink: 0;
  margin-left: auto;
  transition: transform var(--duration-fast) var(--ease-default),
    color var(--duration-fast) var(--ease-default);
}
.chevronOpen {
  transform: rotate(180deg);
}

/* Clear/dismiss × — occupies the chevron slot when the Select has a value or is
   dismissible. Muted by default, brightens on hover. */
.clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: auto;
  color: var(--text-muted);
  cursor: pointer;
  transition: color var(--duration-fast) var(--ease-default);
}

.clear:hover {
  color: var(--text-primary);
}

.iconLeft {
  display: flex;
  align-items: center;
  color: var(--text-muted);
  flex-shrink: 0;
  transition: color var(--duration-fast) var(--ease-default);
}

/* Auto-size to content */
.autoWidth {
  width: auto;
  min-width: 120px;
}
.autoWidth .trigger {
  width: auto;
  min-width: 0;
}

/* ── Dropdown menu (portaled to body) ── */
.menu {
  background: var(--bg-elevated);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  max-height: 240px;
  display: flex;
  flex-direction: column;
}

/* ── Search inside menu ── */
.search {
  padding: var(--space-2);
  border-bottom: 1px solid var(--border-subtle);
  flex-shrink: 0;
}
.searchInput {
  all: unset;
  display: block;
  width: 100%;
  height: 30px;
  padding: 0 var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  color: var(--text-primary);
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--radius-sm);
  box-sizing: border-box;
}
.searchInput::placeholder {
  color: var(--input-placeholder);
}
.searchInput:focus {
  border-color: var(--input-border-focus);
}
/* The menu is already an unambiguous focus context; the global gold :focus-visible
   ring on the search input reads as a stray outline. Border-colour signals focus. */
.searchInput:focus-visible {
  outline: none;
}

/* ── Options list with scroll shadows ── */
.list {
  overflow-y: auto;
  flex: 1;
  padding: var(--space-1) 0;
  position: relative;
}

.listWrap {
  position: relative;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.listWrap::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 24px;
  background: linear-gradient(to bottom, var(--bg-elevated) 0%, transparent 100%);
  pointer-events: none;
  z-index: 1;
  opacity: 0;
  transition: opacity 0.15s;
}

.listWrap::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 24px;
  background: linear-gradient(to top, var(--bg-elevated) 0%, transparent 100%);
  pointer-events: none;
  z-index: 1;
  opacity: 0;
  transition: opacity 0.15s;
}

.listWrap[data-scroll-top='true']::before {
  opacity: 1;
}
.listWrap[data-scroll-bottom='true']::after {
  opacity: 1;
}

/* ── Option item ── */
.option {
  all: unset;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  color: var(--text-secondary);
  cursor: pointer;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  transition: background var(--duration-fast) var(--ease-default),
    color var(--duration-fast) var(--ease-default);
}

/* HUD-style hover: gradient background + left stripe */
.option::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 2px;
  background: var(--accent-secondary);
  opacity: 0;
  transition: opacity var(--duration-fast) var(--ease-default);
}

.option:hover {
  background: linear-gradient(90deg, rgba(240, 180, 41, 0.1) 0%, transparent 70%);
  color: var(--text-primary);
}
.option:hover::before {
  opacity: 1;
}

/* Selected option — HUD-style with gradient and left stripe */
.optionSelected {
  background: linear-gradient(90deg, rgba(240, 180, 41, 0.15) 0%, transparent 70%);
  color: var(--active);
  font-weight: var(--weight-semi);
}
.optionSelected::before {
  opacity: 1;
}
.optionSelected:hover {
  background: linear-gradient(90deg, rgba(240, 180, 41, 0.18) 0%, transparent 70%);
}

.optionLabel {
  flex: 1;
  text-align: left;
}
.checkIcon {
  color: var(--active);
  flex-shrink: 0;
}

.optionImg {
  width: 20px;
  height: 15px;
  object-fit: cover;
  border-radius: 2px;
  flex-shrink: 0;
  display: block;
}

/* React-node icon (e.g. PlatformIcon) in trigger + option rows. */
.optionIcon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* ── Empty state ── */
.empty {
  padding: var(--space-4) var(--space-3);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  color: var(--text-muted);
  text-align: center;
}
